"use client";
import SectionWrapper, { basePx } from "@cc/section-wrapper";
import {
  alpha,
  Box,
  Divider,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowGradient from "@cc/arrow-gradient";
import {
  arrowLeftToRightTransition,
  bottomTextStagger,
  bottomToToptextStaggerChildren,
} from "@cc/animations";
import { H5_1 } from "@theme/components/typography.fontvariant";
import SwiperNavigationButton from "@cc/swiper-navigation-button";
import { useState } from "react";
import { Swiper } from "swiper/types";
import { Navigation } from "swiper/modules";
import EnhancedSwiper from "@cc/enhanced-swiper";
import { MotionTypography } from "@/commonComponents/motion-components";
import { MaterialImage } from "@/commonComponents/material-components";
import { StaticImageData } from "next/image";

export interface BehindTheMissionProps {
  data: {
    title: string;
    slides: ImpactSlidesProps[];
  };
}

const BehindTheMission = (props: BehindTheMissionProps) => {
  const { data } = props;
  const { title, slides } = data;
  const [swiper, setSwiper] = useState<Swiper | null>(null);
  const isMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  return (
    <SectionWrapper
      SectionProps={{
        sx: {
          background: (theme) => theme.palette.gradient.lightToDark,
        },
      }}
      px={{ ...basePx, xs: 0 }}
    >
      <Stack
        direction={"row"}
        width={1}
        alignItems={"center"}
        columnGap={5.8}
        px={{ ...basePx, md: 0, xxl: 0 }}
      >
        {isMd && (
          <ArrowGradient
            SVGProps={{
              width: 56,
              height: 166,
            }}
            {...arrowLeftToRightTransition}
          />
        )}
        <MotionTypography
          component={"h2"}
          variant={"h5"}
          fontSize={H5_1}
          mx={{ xs: "auto", md: 0 }}
          textAlign={{ xs: "center", md: "left" }}
          variants={bottomToToptextStaggerChildren}
          initial="initial"
          whileInView={"animate"}
          viewport={{ once: true }}
        >
          {title}
        </MotionTypography>
        {isMd && <SwiperNavigationButton swiper={swiper} ml={"auto"} />}
      </Stack>
      <EnhancedSwiper
        data={slides}
        SlideComponent={ImpactSlides}
        slidesPerView={"auto"}
        onSwiper={setSwiper}
        SlideWrapperProps={(index) => ({
          sx: {
            width: "auto",
            mt: { xs: "-25px", md: "-35px" },
            pl: { xs: 4, md: index === 0 ? 13 : 10 },
          },
        })}
        navigation={{
          nextEl: ".behind-the-mission-next",
          prevEl: ".behind-the-mission-prev",
        }}
        sx={{
          overflow: { xs: "visible", md: "hidden" },
          pt: { xs: 5, md: 6 },
        }}
        modules={[Navigation]}
        Slots={{
          ContainerStartChildren: (
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              width={1}
              divider={
                <Divider
                  sx={{
                    flexGrow: 1,
                    borderBottom: "2px dashed",
                    borderColor: (theme) =>
                      alpha(theme.palette.common.white, 0.3),
                  }}
                />
              }
            >
              {[1, 2].map((_item, index) => {
                return (
                  <Stack
                    key={index}
                    sx={{
                      width: { xs: 20, md: 25 },
                      height: { xs: 20, md: 25 },
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      background: (theme) =>
                        alpha(theme.palette.common.white, 0.1),
                    }}
                  >
                    <Box
                      component={"span"}
                      width={0.5}
                      height={0.5}
                      bgcolor={"common.white"}
                      borderRadius={"inherit"}
                    />
                  </Stack>
                );
              })}
            </Stack>
          ),
          ContainerEndChildren: isMd ? null : (
            <SwiperNavigationButton
              PrevButtonProps={{
                className: "behind-the-mission-prev",
              }}
              NextButtonProps={{
                className: "behind-the-mission-next",
              }}
            />
          ),
          ContainerEndProps: {
            display: { xs: "flex", md: "none" },
            mt: 5,
            alignItems: "center",
            variants: bottomTextStagger(0),
            initial: "initial",
            whileInView: "animate",
            viewport: { once: true },
          },
        }}
      />
    </SectionWrapper>
  );
};

export default BehindTheMission;

interface ImpactSlidesProps {
  date: string;
  image: StaticImageData | string;
  description: string;
}

const ImpactSlides = (props: ImpactSlidesProps) => {
  const { date, image, description } = props;
  return (
    <Stack rowGap={{ xs: 4, md: 6 }}>
      <Typography
        px={2}
        py={{ xs: 0.75, md: 1.5 }}
        width={"min-content"}
        variant={"body2"}
        sx={{
          background: (theme) => alpha(theme.palette.common.white, 0.2),
          backdropFilter: "blur(4px)",
        }}
      >
        {date}
      </Typography>
      <Stack
        direction={"column"}
        rowGap={2}
        alignItems={"stretch"}
        width={{ xs: 272, md: 310 }}
      >
        <MaterialImage
          src={image}
          alt={""}
          width={310}
          height={220}
          sx={{
            width: { xs: 272, md: 310 },
            height: { xs: 240, md: 220 },
            objectFit: "cover",
          }}
        />
        <MotionTypography className="description" textAlign={"justify"} variant="body1">
          {description}
        </MotionTypography>
      </Stack>
    </Stack>
  );
};
